import { EnergyIcon, EnergyQuestion } from '../types/energy.types';

export const ENERGY_ICONS: EnergyIcon[] = [
  {
    id: 'fall',
    emoji: '🧍⬇️',
    title: 'Fall from height',
    desc: 'Person could fall.',
  },
  {
    id: 'vehicle',
    emoji: '🚜',
    title: 'Vehicle / mobile plant',
    desc: 'Moving plant or traffic.',
  },
  {
    id: 'suspended',
    emoji: '🏗️',
    title: 'Suspended / lifted load',
    desc: 'Load could drop or swing.',
  },
  {
    id: 'pressure',
    emoji: '💨',
    title: 'Stored pressure',
    desc: 'Hydraulics, pneumatics, vessels, hoses.',
  },
  {
    id: 'electrical',
    emoji: '⚡',
    title: 'Electrical exposure',
    desc: 'Live conductors, panels, testing.',
  },
  {
    id: 'machinery',
    emoji: '⚙️',
    title: 'Rotating / moving machinery',
    desc: 'Pinch, entangle, crush points.',
  },
  {
    id: 'stored_mech',
    emoji: '🪢',
    title: 'Stored mechanical energy',
    desc: 'Tensioned lines, springs, winches.',
  },
  {
    id: 'excavation',
    emoji: '🕳️',
    title: 'Excavation / collapse',
    desc: 'Trench or ground instability.',
  },
  {
    id: 'chemical_thermal',
    emoji: '🔥',
    title: 'Chemical / thermal',
    desc: 'Tag only (not joule-based here).',
  },
  {
    id: 'biological',
    emoji: '🦠',
    title: 'Biological hazard',
    desc: 'Bacteria, viruses, mold, animal/insect exposure.',
  },
];

export const ENERGY_QUESTIONS: EnergyQuestion[] = [
  // === FALL FROM HEIGHT ===
  // 1. Magnitude
  {
    id: 'fall_magnitude',
    icon: 'fall',
    label: '1. Fall height category?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'low', label: 'Low (< 1.2m - minor injury)' },
      { value: 'medium', label: 'Medium (1.2-2m - serious injury)' },
      { value: 'high', label: 'High (2-6m - major injury)' },
      { value: 'severe', label: 'Severe (> 6m or onto hazard - fatal)' },
    ],
    default: 'not_sure',
  },
  // 2. Exposure
  {
    id: 'fall_exposure',
    icon: 'fall',
    label: '2. Could a person actually fall?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'no_barrier', label: 'No - physical barrier prevents access' },
      {
        value: 'no_admin',
        label: 'No - permit/restricted area (administrative)',
      },
      { value: 'yes', label: 'Yes - people work at height' },
    ],
    default: 'not_sure',
  },
  // 3. Controls
  {
    id: 'fall_controls',
    icon: 'fall',
    label: '3. What controls are in place?',
    type: 'select',
    options: [
      { value: 'none', label: 'None / Not sure' },
      { value: 'ppe', label: 'PPE only (harness/lanyard)' },
      { value: 'prevention', label: 'Fall prevention (guardrails/barriers)' },
      { value: 'elimination', label: 'Work elimination (no work at height)' },
    ],
    default: 'none',
  },

  // === VEHICLE / MOBILE PLANT ===
  // 1. Magnitude
  {
    id: 'vehicle_magnitude',
    icon: 'vehicle',
    label: '1. Vehicle/plant mass and speed?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'low', label: 'Low (light vehicle, slow manoeuvring)' },
      { value: 'medium', label: 'Medium (vehicle at site road speed)' },
      { value: 'high', label: 'High (heavy plant or highway speed)' },
      { value: 'severe', label: 'Severe (large machinery, multiple hazards)' },
    ],
    default: 'not_sure',
  },
  // 2. Exposure
  {
    id: 'vehicle_exposure',
    icon: 'vehicle',
    label: '2. Could people be in the path of the vehicle?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      {
        value: 'no_barrier',
        label: 'No - physical separation (barriers/exclusion)',
      },
      {
        value: 'no_admin',
        label: 'No - administrative separation (permits/spotters)',
      },
      { value: 'yes', label: 'Yes - people in line-of-fire' },
    ],
    default: 'not_sure',
  },
  // 3. Controls
  {
    id: 'vehicle_controls',
    icon: 'vehicle',
    label: '3. What traffic controls are in place?',
    type: 'select',
    options: [
      { value: 'none', label: 'None / Not sure' },
      { value: 'ppe', label: 'PPE/visibility only (hi-vis)' },
      {
        value: 'admin',
        label: 'Administrative (traffic plan/spotter/permits)',
      },
      {
        value: 'separation',
        label: 'Physical separation (barriers/designated areas)',
      },
    ],
    default: 'none',
  },

  // === SUSPENDED / LIFTED LOAD ===
  // 1. Magnitude
  {
    id: 'suspended_magnitude',
    icon: 'suspended',
    label: '1. Load weight and lift height?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'low', label: 'Low (light load, low lift)' },
      { value: 'medium', label: 'Medium (moderate load or height)' },
      { value: 'high', label: 'High (heavy load or significant height)' },
      { value: 'severe', label: 'Severe (very heavy or complex lift)' },
    ],
    default: 'not_sure',
  },
  // 2. Exposure
  {
    id: 'suspended_exposure',
    icon: 'suspended',
    label: '2. Could the load fall on or strike someone?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'no', label: 'No - exclusion zone maintained' },
      { value: 'yes', label: 'Yes - people under or near load path' },
    ],
    default: 'not_sure',
  },
  // 3. Controls
  {
    id: 'suspended_controls',
    icon: 'suspended',
    label: '3. What lifting controls are in place?',
    type: 'select',
    options: [
      { value: 'none', label: 'None / Not sure' },
      { value: 'basic', label: 'Basic (rated gear, no formal plan)' },
      { value: 'standard', label: 'Standard (lift plan, trained operator)' },
      {
        value: 'comprehensive',
        label: 'Comprehensive (lift plan, permit, exclusion zone)',
      },
    ],
    default: 'none',
  },

  // === STORED PRESSURE ===
  // 1. Magnitude
  {
    id: 'pressure_magnitude',
    icon: 'pressure',
    label: '1. System pressure level?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'low', label: 'Low (< 500 psi)' },
      { value: 'medium', label: 'Medium (500-2000 psi)' },
      { value: 'high', label: 'High (2000-5000 psi)' },
      { value: 'severe', label: 'Severe (> 5000 psi)' },
    ],
    default: 'not_sure',
  },
  // 2. Exposure
  {
    id: 'pressure_exposure',
    icon: 'pressure',
    label: '2. Could pressure release impact a person?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'no', label: 'No - isolated/contained/depressurised' },
      { value: 'yes', label: 'Yes - hose whip, spray, or release possible' },
    ],
    default: 'not_sure',
  },
  // 3. Controls
  {
    id: 'pressure_controls',
    icon: 'pressure',
    label: '3. What pressure controls are in place?',
    type: 'select',
    options: [
      { value: 'none', label: 'None / Not sure' },
      { value: 'ppe', label: 'PPE only (face shield, gloves)' },
      {
        value: 'procedures',
        label: 'Procedures (rated components, stand clear)',
      },
      { value: 'isolation', label: 'Isolation (depressurised, locked out)' },
    ],
    default: 'none',
  },

  // === ELECTRICAL EXPOSURE ===
  // 1. Magnitude
  {
    id: 'electrical_magnitude',
    icon: 'electrical',
    label: '1. Voltage level?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'low', label: 'Low (< 50V - minor shock)' },
      { value: 'medium', label: 'Medium (50-415V - serious shock)' },
      { value: 'high', label: 'High (415V-11kV - major injury)' },
      { value: 'severe', label: 'Severe (> 11kV - arc flash/fatal)' },
    ],
    default: 'not_sure',
  },
  // 2. Exposure
  {
    id: 'electrical_exposure',
    icon: 'electrical',
    label: '2. Could someone contact live parts?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'no', label: 'No - verified isolated and locked out' },
      { value: 'yes', label: 'Yes - live parts accessible' },
    ],
    default: 'not_sure',
  },
  // 3. Controls
  {
    id: 'electrical_controls',
    icon: 'electrical',
    label: '3. What electrical controls are in place?',
    type: 'select',
    options: [
      { value: 'none', label: 'None / Not sure' },
      { value: 'ppe', label: 'PPE only (insulated tools/gloves)' },
      { value: 'procedures', label: 'Procedures (authorised person, testing)' },
      { value: 'isolation', label: 'Isolation (LOTO, tested dead, earthed)' },
    ],
    default: 'none',
  },

  // === ROTATING / MOVING MACHINERY ===
  // 1. Magnitude
  {
    id: 'machinery_magnitude',
    icon: 'machinery',
    label: '1. Machinery force/speed?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'low', label: 'Low (slow/light pinch points)' },
      { value: 'medium', label: 'Medium (moderate crush/entangle risk)' },
      { value: 'high', label: 'High (heavy/fast - severe injury)' },
      { value: 'severe', label: 'Severe (high force - likely fatal)' },
    ],
    default: 'not_sure',
  },
  // 2. Exposure
  {
    id: 'machinery_exposure',
    icon: 'machinery',
    label: '2. Can someone reach moving parts while running?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'no', label: 'No - fully guarded or machine is off' },
      { value: 'yes', label: 'Yes - accessible while operating' },
    ],
    default: 'not_sure',
  },
  // 3. Controls
  {
    id: 'machinery_controls',
    icon: 'machinery',
    label: '3. What machinery controls are in place?',
    type: 'select',
    options: [
      { value: 'none', label: 'None / Not sure' },
      { value: 'ppe', label: 'PPE only (awareness/training)' },
      { value: 'guarding', label: 'Guarding (interlocked guards present)' },
      { value: 'isolation', label: 'Isolation (machine LOTO before access)' },
    ],
    default: 'none',
  },

  // === STORED MECHANICAL ENERGY ===
  // 1. Magnitude
  {
    id: 'stored_mech_magnitude',
    icon: 'stored_mech',
    label: '1. Stored energy level?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'low', label: 'Low (light tension, small spring)' },
      { value: 'medium', label: 'Medium (moderate tension/compression)' },
      { value: 'high', label: 'High (high tension lines, heavy springs)' },
      { value: 'severe', label: 'Severe (extreme tension - snap-back risk)' },
    ],
    default: 'not_sure',
  },
  // 2. Exposure
  {
    id: 'stored_mech_exposure',
    icon: 'stored_mech',
    label: '2. If released, could it strike someone?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'no', label: 'No - restrained or exclusion zone' },
      { value: 'yes', label: 'Yes - in potential strike path' },
    ],
    default: 'not_sure',
  },
  // 3. Controls
  {
    id: 'stored_mech_controls',
    icon: 'stored_mech',
    label: '3. What stored energy controls are in place?',
    type: 'select',
    options: [
      { value: 'none', label: 'None / Not sure' },
      { value: 'awareness', label: 'Awareness (trained, stand clear)' },
      { value: 'restraint', label: 'Restraint (secured/controlled release)' },
      {
        value: 'elimination',
        label: 'Elimination (fully released/de-energised)',
      },
    ],
    default: 'none',
  },

  // === EXCAVATION / COLLAPSE ===
  // 1. Magnitude
  {
    id: 'excavation_magnitude',
    icon: 'excavation',
    label: '1. Excavation depth and soil type?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'low', label: 'Low (shallow, stable soil)' },
      { value: 'medium', label: 'Medium (waist-deep or unstable)' },
      { value: 'high', label: 'High (over-head depth)' },
      { value: 'severe', label: 'Severe (deep, unstable, confined)' },
    ],
    default: 'not_sure',
  },
  // 2. Exposure
  {
    id: 'excavation_exposure',
    icon: 'excavation',
    label: '2. Will people enter or work in the excavation?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'no', label: 'No - no entry required' },
      { value: 'yes', label: 'Yes - entry required' },
    ],
    default: 'not_sure',
  },
  // 3. Controls
  {
    id: 'excavation_controls',
    icon: 'excavation',
    label: '3. What excavation controls are in place?',
    type: 'select',
    options: [
      { value: 'none', label: 'None / Not sure' },
      { value: 'basic', label: 'Basic (edge protection, services located)' },
      { value: 'shoring', label: 'Shoring/benching (protection system)' },
      {
        value: 'comprehensive',
        label: 'Comprehensive (permit, shoring, competent person)',
      },
    ],
    default: 'none',
  },

  // === BIOLOGICAL HAZARD ===
  // 1. Magnitude
  {
    id: 'biological_magnitude',
    icon: 'biological',
    label: '1. Biological hazard severity?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      { value: 'low', label: 'Low (general environment, low risk)' },
      { value: 'medium', label: 'Medium (possible contamination, mold)' },
      { value: 'high', label: 'High (confirmed contamination, sewage)' },
      { value: 'severe', label: 'Severe (bloodborne, disease outbreak)' },
    ],
    default: 'not_sure',
  },
  // 2. Exposure
  {
    id: 'biological_exposure',
    icon: 'biological',
    label: '2. Could people contact contaminated materials/spaces?',
    type: 'select',
    options: [
      { value: 'not_sure', label: 'Not sure' },
      {
        value: 'no_barrier',
        label: 'No - physical containment/barrier prevents contact',
      },
      {
        value: 'no_admin',
        label: 'No - administrative controls (restricted access)',
      },
      { value: 'yes', label: 'Yes - potential for contact/exposure' },
    ],
    default: 'not_sure',
  },
  // 3. Controls
  {
    id: 'biological_controls',
    icon: 'biological',
    label: '3. What biological controls are in place?',
    type: 'select',
    options: [
      { value: 'none', label: 'None / Not sure' },
      { value: 'ppe', label: 'PPE only (gloves/masks)' },
      {
        value: 'procedures',
        label: 'Procedures (hygiene protocols, vaccination)',
      },
      {
        value: 'elimination',
        label: 'Engineering controls (ventilation, decontamination)',
      },
    ],
    default: 'none',
  },
];
